import type { TranscriptResponse } from '@/client/types.gen'
import { useTranslation } from '@/hooks/useTranslation'

export interface TranscriptMetadataContentProps {
	transcript: TranscriptResponse | null | undefined
}

export function TranscriptMetadataContent({ transcript }: TranscriptMetadataContentProps) {
	const { t } = useTranslation()

	if (!transcript?.metadata) return null

	const { metadata } = transcript

	return (
		<div className="space-y-4 pt-4 border-t">
			<h3 className="text-sm font-semibold text-foreground">
				{t('pages.projectDetail.transcript.metadata.title')}
			</h3>
			<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
				{metadata.duration_seconds != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.duration')}
						</p>
						<p className="text-sm text-foreground">
							{Math.floor(metadata.duration_seconds / 60)}:
							{Math.floor(metadata.duration_seconds % 60)
								.toString()
								.padStart(2, '0')}
						</p>
					</div>
				)}
				{metadata.language && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.language')}
						</p>
						<p className="text-sm text-foreground">{metadata.language}</p>
					</div>
				)}
				{metadata.confidence != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.confidence')}
						</p>
						<p className="text-sm text-foreground">{(metadata.confidence * 100).toFixed(1)}%</p>
					</div>
				)}
				{metadata.processed_at && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.processedAt')}
						</p>
						<p className="text-sm text-foreground">{new Date(metadata.processed_at).toLocaleString()}</p>
					</div>
				)}
				{metadata.word_count != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.wordCount')}
						</p>
						<p className="text-sm text-foreground">{metadata.word_count.toLocaleString()}</p>
					</div>
				)}
				{metadata.character_count != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.characterCount')}
						</p>
						<p className="text-sm text-foreground">{metadata.character_count.toLocaleString()}</p>
					</div>
				)}
				{metadata.speaker_count != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.speakerCount')}
						</p>
						<p className="text-sm text-foreground">{metadata.speaker_count}</p>
					</div>
				)}
				{metadata.utterance_count != null && (
					<div className="space-y-2">
						<p className="text-sm font-medium text-muted-foreground">
							{t('pages.projectDetail.transcript.metadata.utteranceCount')}
						</p>
						<p className="text-sm text-foreground">{metadata.utterance_count.toLocaleString()}</p>
					</div>
				)}
			</div>
			{transcript.speakers && transcript.speakers.length > 0 && (
				<div className="space-y-2 pt-2">
					<p className="text-sm font-medium text-muted-foreground">
						{t('pages.projectDetail.transcript.metadata.speakers')}
					</p>
					<div className="flex flex-wrap gap-2">
						{transcript.speakers.map((speaker) => (
							<span
								key={speaker}
								className="inline-flex items-center px-2 py-1 rounded-md bg-[#8c52ff]/10 text-[#8c52ff] text-sm font-medium"
							>
								{speaker}
							</span>
						))}
					</div>
				</div>
			)}
		</div>
	)
}
